import { useCallback, useState } from "react";
import { Check, Flag, X, Minus } from "lucide-react";
import type { StatusCellProps } from "./types";

/** Pastel badge colors: bg + text + border */
const STATUS_STYLES: Record<number, { badge: string; icon: string }> = {
  0: {
    badge: "bg-slate-100 border-slate-200 text-slate-500",
    icon: "text-slate-400",
  },
  1: {
    badge: "bg-emerald-50 border-emerald-200 text-emerald-700",
    icon: "text-emerald-500",
  },
  2: {
    badge: "bg-amber-50 border-amber-200 text-amber-700",
    icon: "text-amber-500",
  },
  3: {
    badge: "bg-rose-50 border-rose-200 text-rose-700",
    icon: "text-rose-400",
  },
};

/** Row tint classes — applied to the parent <tr> via data attribute */
export const STATUS_ROW_COLORS: Record<number, string> = {
  0: "",
  1: "bg-emerald-50/40",
  2: "bg-amber-50/40",
  3: "bg-rose-50/30",
};

const STATUS_ICONS: Record<
  number,
  React.ComponentType<{ className?: string }>
> = {
  0: Minus,
  1: Check,
  2: Flag,
  3: X,
};

function getStyle(status: number | undefined | null) {
  return STATUS_STYLES[status ?? 0] || STATUS_STYLES[0];
}

function getStatusIcon(status: number | undefined | null) {
  return STATUS_ICONS[status ?? 0] || STATUS_ICONS[0];
}

/** Cycle: Pending → Confirmed → Flagged → Cancelled → Pending */
function cycleStatus(status: number | undefined | null): number {
  return ((status ?? 0) + 1) % 4;
}

/**
 * Display-first status cell. Renders a colored badge with a click-to-cycle
 * handler. The previous Radix `DropdownMenu` listing all four statuses was
 * dropped — every row was paying the Radix Presence forced-reflow cost
 * (~12 ms each, see PartnerGroup open trace) for a control that
 * 99 % of users invoke via click-cycle anyway.
 */
export function StatusCell({ flow, disabled, onFlowUpdate }: StatusCellProps) {
  const [animating, setAnimating] = useState(false);

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (disabled) return;
      setAnimating(true);
      onFlowUpdate(flow.id, { status: cycleStatus(flow.status) });
      setTimeout(() => setAnimating(false), 150);
    },
    [disabled, onFlowUpdate, flow.id, flow.status],
  );

  const style = getStyle(flow.status);
  const Icon = getStatusIcon(flow.status);

  return (
    <button
      data-testid="flow-status-button"
      onClick={handleClick}
      onContextMenu={(e) => e.preventDefault()}
      disabled={disabled}
      className={`w-8 h-8 rounded-md border ${style.badge} ${
        disabled ? "cursor-default" : "cursor-pointer"
      } flex-shrink-0 transition-all duration-150 flex items-center justify-center ${
        animating
          ? "scale-110 shadow-md ring-2 ring-blue-200 ring-offset-1"
          : "scale-100 hover:shadow-sm"
      }`}
      title={`Status: ${
        (flow.status ?? 0) === 0
          ? "Pending"
          : flow.status === 1
            ? "Confirmed"
            : flow.status === 2
              ? "Flagged"
              : "Cancelled"
      }`}
    >
      <Icon className={`h-4 w-4 ${style.icon}`} />
    </button>
  );
}
